import { ChevronLeft, MessageSquarePlus, Trash2 } from "lucide-react";
import type { SavedConversation } from "./threadSerialization";
import { EditableConversationTitle } from "./EditableConversationTitle";
import { ConversationActions } from "./ConversationActions";

/** Overlay listing saved conversations with select/delete/new actions. */
export function ConversationsPanel({
  conversations,
  onSelect,
  onDelete,
  onRename,
  onClose,
  onNew,
}: {
  conversations: SavedConversation[];
  onSelect: (conv: SavedConversation) => void;
  onDelete: (id: string) => void;
  onRename: (id: string, title: string) => Promise<void>;
  onClose: () => void;
  onNew: () => void;
}) {
  return (
    <div className="absolute inset-0 z-10 flex flex-col bg-neutral-850">
      <div className="flex shrink-0 items-center justify-between border-b border-neutral-700/60 px-4 py-2">
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="rounded-md p-1 text-neutral-500 transition-colors hover:bg-neutral-800 hover:text-neutral-200"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm font-semibold text-neutral-100">
            Conversations
          </span>
        </div>
        <ConversationActions
          onNew={onNew}
          onToggleHistory={() => {}}
          hideHistory
        />
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-neutral-700">
        {conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 p-8 text-center">
            <MessageSquarePlus className="h-8 w-8 text-neutral-700" />
            <p className="text-sm text-neutral-500">
              No saved conversations yet.
            </p>
            <button
              onClick={onNew}
              className="text-xs text-neutral-400 hover:text-neutral-300"
            >
              Start a new chat
            </button>
          </div>
        ) : (
          <div className="space-y-0.5 p-2">
            {conversations.map((conv) => (
              <div
                key={conv.id}
                className="group rounded-lg px-2 py-1 hover:bg-neutral-900 hover:cursor-pointer"
                onClick={() => onSelect(conv)}
              >
                <div className="flex min-w-0 items-center gap-1">
                  <EditableConversationTitle
                    title={conv.title}
                    onSave={(title) => onRename(conv.id, title)}
                    showEditButton
                    editOnTitleClick={false}
                  />
                  <button
                    onClick={(event) => {
                      event.stopPropagation();
                      onDelete(conv.id);
                    }}
                    className="shrink-0 rounded-md p-1 text-neutral-700 opacity-0 transition-all group-hover:opacity-100 hover:bg-red-500/10 hover:text-red-400 focus:opacity-100"
                    title="Delete conversation"
                    aria-label="Delete conversation"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                <span className="mt-0.5 flex items-center gap-1 text-[10px] text-neutral-600">
                  {conv.createdAt.toLocaleDateString()} at{" "}
                  {conv.createdAt.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
